import { Button } from '@heroui/react'
import { type ModalComponentProps } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { CheckIcon, toast } from '@qse/ui'
import { Copy } from 'lucide-react'
import { useEffect, useState } from 'react'

export default function ApiKeyDetails(props: ModalComponentProps) {
  const { setModelActive, onClose, data: dataProps } = props
  const [isCopied, setIsCopied] = useState(false)
  const [showCheckIcon, setShowCheckIcon] = useState(false)
  const { setData } = useActiveModelStore()

  const data = dataProps?.data || dataProps

  // This function will be called by the modal before any closing attempt
  const handleBeforeClose = () => {
    if (!isCopied) {
      toast.warning('You must copy the key before leaving this page.')

      return false // Prevent closing
    }

    return true // Allow closing
  }

  const handleCancel = () => {
    if (!isCopied) {
      toast.warning('You must copy the key before leaving this page.')
    } else {
      setModelActive(undefined)
      onClose()
    }
  }

  const handleCopy = () => {
    setShowCheckIcon(true)
    setTimeout(() => {
      setShowCheckIcon(false)
    }, 2000)
    navigator.clipboard.writeText(data?.key)
    setIsCopied(true)
    toast.success('The API key has been copied to your clipboard')
  }

  useEffect(() => {
    // Set the onBeforeClose callback in the modal data
    // This will be used by the modal component to validate before closing
    if (!isCopied) {
      setData({
        ...data,
        onBeforeClose: handleBeforeClose,
      })
    } else {
      setData({
        ...data,
        onBeforeClose: null,
      })
    }
  }, [isCopied])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isCopied) {
        event.preventDefault()
        event.returnValue = 'You must copy the key before leaving this page.'

        return event.returnValue
      }
    }

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Cleanup event listeners
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isCopied])

  return (
    <div className="min-h-[300px]">
      <div className="py-8 px-6 flex flex-col gap-8">
        {/* Critical Warning Section */}
        <div className="bg-blue-50 dark:bg-[#2196F31A] rounded-[10px] p-[10px] flex items-center gap-2 border-blue-200 dark:border-[#2196F3] border-1">
          <img alt="info" className="w-14" src={'/assets/svg/warning.svg'} />

          <p>
            <span className="font-bold">ONE TIME ONLY:</span> This is your only chance to view and
            save your keypair. It will not be shown again for security reasons.
          </p>
        </div>

        {/* API Key Display Section */}
        <div className="space-y-4">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">API Key</div>
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <div className="flex items-center justify-between gap-2">
              <code className="text-sm text-gray-900 dark:text-white font-mono break-all">
                {data?.key}
              </code>
              <button
                title="Copy API key"
                type="button"
                className={`p-2 rounded-md transition-all duration-200 ${
                  showCheckIcon
                    ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                onClick={handleCopy}
              >
                {showCheckIcon ? <CheckIcon className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Copy Status */}
          {isCopied && (
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <CheckIcon className="w-4 h-4" />
              <span className="font-medium">API key copied successfully!</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            className="px-8 py-3 font-medium"
            color="default"
            radius="lg"
            size="lg"
            onPress={handleCancel}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}
